import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Input from './styled/Input';
import Button from './styled/Button';

const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    language: i18n.language
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setSubmitStatus({ type: 'error', message: t('contact.form.errors.nameRequired') });
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      setSubmitStatus({ type: 'error', message: t('contact.form.errors.emailInvalid') });
      return false;
    }
    if (!formData.subject.trim()) {
      setSubmitStatus({ type: 'error', message: t('contact.form.errors.subjectRequired') });
      return false;
    }
    if (!formData.message.trim()) {
      setSubmitStatus({ type: 'error', message: t('contact.form.errors.messageRequired') });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitStatus({ 
        type: 'success', 
        message: t('contact.form.success') 
      });
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        language: i18n.language
      });
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: t('contact.form.error') 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {submitStatus && (
        <div className={`p-4 rounded-md ${
          submitStatus.type === 'success' 
            ? 'bg-green-50 text-green-800 border border-green-200' 
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {submitStatus.message}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label={t('contact.form.name')}
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder={t('contact.form.namePlaceholder')}
          fullWidth
        />
        
        <Input
          label={t('contact.form.email')}
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder={t('contact.form.emailPlaceholder')}
          fullWidth
        />
      </div>
      
      <Input
        label={t('contact.form.subject')}
        name="subject"
        value={formData.subject}
        onChange={handleChange}
        placeholder={t('contact.form.subjectPlaceholder')}
        fullWidth
      />
      
      <Input
        label={t('contact.form.message')}
        name="message"
        value={formData.message}
        onChange={handleChange}
        placeholder={t('contact.form.messagePlaceholder')}
        type="textarea"
        rows={6}
        fullWidth
      />
      
      <div className="flex items-center">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={isSubmitting}
          className="min-w-[120px]"
        >
          {isSubmitting ? t('contact.form.sending') : t('contact.form.send')}
        </Button>
        
        {isSubmitting && (
          <div className="ml-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-hemp-green-600"></div>
          </div>
        )}
      </div>
    </form>
  );
};

export default ContactForm;
